import type { DealInput, CalculatedMetrics } from '@/lib/schema'
import { formatCurrency, formatPercent, formatNumber } from '@/lib/formulas'
import { clsx } from 'clsx'

interface DealMetricsProps {
  deal: DealInput
  metrics: CalculatedMetrics
  className?: string
}

interface MetricCardProps {
  label: string
  value: string
  subValue?: string
  variant?: 'default' | 'success' | 'warning' | 'danger'
}

function MetricCard({ label, value, subValue, variant = 'default' }: MetricCardProps) {
  return (
    <div className="metric-card">
      <p className="metric-label">{label}</p>
      <p
        className={clsx(
          'metric-value',
          variant === 'success' && 'text-success-600',
          variant === 'warning' && 'text-warning-600',
          variant === 'danger' && 'text-danger-600'
        )}
      >
        {value}
      </p>
      {subValue && <p className="mt-1 text-xs text-slate-400">{subValue}</p>}
    </div>
  )
}

export function DealMetrics({ deal, metrics, className }: DealMetricsProps) {
  // Determine color variants based on metric values
  const capRateVariant =
    metrics.capRate >= 8 ? 'success' : metrics.capRate >= 6 ? 'warning' : 'danger'
  const cashOnCashVariant =
    metrics.cashOnCash >= 10 ? 'success' : metrics.cashOnCash >= 8 ? 'warning' : 'danger'
  const cashFlowVariant =
    metrics.monthlyCashFlow >= 200 ? 'success' : metrics.monthlyCashFlow >= 0 ? 'warning' : 'danger'
  const dscrVariant = metrics.dscr >= 1.25 ? 'success' : metrics.dscr >= 1.0 ? 'warning' : 'danger'

  return (
    <div className={clsx('space-y-6', className)}>
      {/* Key Metrics */}
      <div>
        <h2 className="mb-4 text-lg font-semibold text-slate-900">Key Metrics</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            label="Cap Rate"
            value={formatPercent(metrics.capRate)}
            subValue="NOI / Purchase Price"
            variant={capRateVariant}
          />
          <MetricCard
            label="Cash-on-Cash Return"
            value={formatPercent(metrics.cashOnCash)}
            subValue="Annual Cash Flow / Cash Invested"
            variant={cashOnCashVariant}
          />
          <MetricCard
            label="Monthly Cash Flow"
            value={formatCurrency(metrics.monthlyCashFlow)}
            subValue={`${formatCurrency(metrics.annualCashFlow)}/year`}
            variant={cashFlowVariant}
          />
          <MetricCard
            label="DSCR"
            value={formatNumber(metrics.dscr)}
            subValue="Income / Debt Service"
            variant={dscrVariant}
          />
        </div>
      </div>

      {/* Investment Summary */}
      <div>
        <h2 className="mb-4 text-lg font-semibold text-slate-900">Investment Summary</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            label="Purchase Price"
            value={formatCurrency(deal.purchasePrice)}
          />
          <MetricCard
            label="Down Payment"
            value={formatCurrency(metrics.downPayment)}
            subValue={`${deal.downPaymentPercent}%`}
          />
          <MetricCard
            label="Loan Amount"
            value={formatCurrency(metrics.loanAmount)}
          />
          <MetricCard
            label="Total Cash Invested"
            value={formatCurrency(metrics.totalCashInvested)}
            subValue="Down + Closing + Rehab"
          />
        </div>
      </div>

      {/* Income & Expenses */}
      <div>
        <h2 className="mb-4 text-lg font-semibold text-slate-900">Income & Expenses</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            label="Gross Monthly Income"
            value={formatCurrency(metrics.grossMonthlyIncome)}
            subValue="Rent + Other"
          />
          <MetricCard
            label="Effective Gross Income"
            value={formatCurrency(metrics.effectiveGrossIncome)}
            subValue={`Annual after ${deal.vacancyRate}% vacancy`}
          />
          <MetricCard
            label="Monthly Expenses"
            value={formatCurrency(metrics.totalMonthlyExpenses)}
            subValue="Operating expenses"
          />
          <MetricCard
            label="NOI"
            value={formatCurrency(metrics.noi)}
            subValue="Annual Net Operating Income"
          />
        </div>
      </div>

      {/* Loan Details */}
      <div>
        <h2 className="mb-4 text-lg font-semibold text-slate-900">Loan Details</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            label="Monthly Payment (P&I)"
            value={formatCurrency(metrics.monthlyPayment)}
          />
          <MetricCard
            label="Annual Debt Service"
            value={formatCurrency(metrics.annualDebtService)}
          />
          <MetricCard
            label="Interest Rate"
            value={formatPercent(deal.interestRate)}
          />
          <MetricCard
            label="GRM"
            value={formatNumber(metrics.grm)}
            subValue="Gross Rent Multiplier"
          />
        </div>
      </div>
    </div>
  )
}
